import type { LineTransport } from './transport';

/** Quotes (F22) as the API sends them. Fields marked `?` are absent for a role that may not see amounts. */

import { formatDecimal, parseScaled } from '../ui/numbers';
export const QUOTES_RESOURCE = 'quotes.quotes';
export const LINES_RESOURCE = 'quotes.lines';
export const TOTALS_RESOURCE = 'quotes.totals';

export type VersionState = 'robocza' | 'wyslana' | 'zaakceptowana' | 'odrzucona';
export const STATE_LABELS: Record<VersionState, string> = { robocza: 'robocza', wyslana: 'wysłana', zaakceptowana: 'zaakceptowana', odrzucona: 'odrzucona' };
/** P8 (owner note 00:21): why the client rejected a version. */
export type RejectReason = 'rezygnacja_klienta' | 'za_drogo' | 'inny_wykonawca' | 'inne';
export const REJECT_REASON_LABELS: Record<RejectReason, string> = { rezygnacja_klienta: 'Rezygnacja klienta', za_drogo: 'Za drogo', inny_wykonawca: 'Inny wykonawca', inne: 'Inne' };

export type QuoteLine = {
  /** „Dni rezerwacji…” of our gear (owner 2026-10-24 15:29): the event's days of the item (the packlist's); absent when none. */
  extraDays?: { before: boolean; after: boolean; dates: string[] };
  extraHeld?: string[];
  /** That setting's version (0: never set): sent back by „Dni rezerwacji…” (409 when stale). */
  extraDaysVersion?: number;
  id: string;
  sectionId: string;
  sort: number;
  type: 'item' | 'package' | 'service' | 'custom';
  itemId: string | null;
  packageId: string | null;
  serviceId: string | null;
  name: string;
  unitPriceGrosze?: number;
  quantity: number;
  daysMilli: number;
  linear: boolean;
  discountBp?: number;
  description: string;
  day: string | null;
  origin: string;
  qtyOverridden: boolean;
  /** P15: what the rules propose for the service now ("reguła proponuje N"); null when nothing, absent when hidden. */
  ruleQuantity?: number | null;
  /** Obsada → kosztorys: how many people of the function the crew has for the line ("obsada: N"); absent when hidden. */
  crewQuantity?: number | null;
  /** Owner 2026-10-25 01:56: the names behind a crew line (the hover), empty when nobody — absent without the crew's people (rule 7a). */
  crewPeople?: string[];
  /** Transport priced by the event's distance (P5), otherwise null. */
  transport?: LineTransport | null;
  /** The price was typed by hand: a new distance leaves it alone. */
  priceOverridden?: boolean;
  netGrosze?: number;
  /** F29: the company's cost (sensitive; absent when hidden); null when not known. */
  costGrosze?: number | null;
  /** F29: the cost was typed by hand (it no longer follows the rate). */
  costOverridden?: boolean;
  /** F28: the cost is the cheapest supplier's price (only with the right to suppliers' prices) and that supplier. */
  costFromSupplier?: boolean;
  /** 2026-10-24: how own transport's cost per km was counted („ON 6,49 zł/l × 9 l/100 km = 0,58 zł/km”); with the cost only. */
  costBasis?: string;
  costSupplier?: string;
  /**
   * Owner 2026-10-21 00:32 (shown with the costs only): added from a supplier's catalog — red on screen, its supplier
   * in the "Dostawca" column and its rental in "Dopożyczenia" (the rental's cost is the line's).
   */
  supplierId?: string | null;
  /** The supplier's position the line was added from (Ctrl+klik opens it); hidden with the supplier. */
  supplierPriceId?: string | null;
  supplierName?: string;
  rentalId?: string | null;
  /** Review N1: no typed cost and the supplier has no position of it (a line saved before 2026-10-23) — „<Dostawca> nie ma tej pozycji — dodaj ją w katalogu dostawcy”. */
  noSupplierPrice?: boolean;
  /** Review I-new-1: the rental is picked up or returned — the cost is its actual one, changed in "Dopożyczenia". */
  rentalLocked?: boolean;
  /**
   * Owner 2026-11-01: the line's rental of a closed event — `anulowane`: cancelled with it (struck through, red tag, the
   * line costs nothing); `sprawdz`: picked up before („… – sprawdź rozliczenie z dostawcą”).
   */
  rentalMark?: { kind: 'anulowane' | 'sprawdz'; reason?: string; label: string };
  /** Review m4: the line's quantity or supplier differs from the picked up / returned rental. */
  rentalMismatch?: boolean;
  /** Pkt 3 (review): the rental was clamped to the supplier's stock — how many pieces it lacks. */
  rentalShort?: number;
  /**
   * Owner 2026-10-23 22:24 (with the costs only): the event's rentals of this item — made in the packlist, in
   * „Dopożyczenia” or from the quote — on the item's first own line; their summed cost is in `costGrosze` too.
   */
  rentals?: LineRental[];
  /** Owner 2026-10-24 16:15 (pkt 6): for a role without costs — the same rentals as pieces per supplier only (no amounts). */
  rentalPieces?: Array<Pick<LineRental, 'id' | 'supplierId' | 'supplierName' | 'quantity' | 'status'>>;
  rentalCostGrosze?: number;
  /** Owner 21:05 (with „Dostępność”): under the item's first own line — what the whole version lacks and the days a rental takes. */
  /** Owner 2026-10-25 00:39: `holders` — who holds the item over those days (on hover of „brakuje N”; rule 7a by the server). */
  shortage?: { missing: number; firstDay: string; lastDay: string; holders?: ShortageHolder[] };
  /** Owner 21:0x / 2026-10-24 (with the costs): that shortage's estimated cost — in `costGrosze` too (red, explained on hover). */
  estimatedCostGrosze?: number;
  /** Owner 2026-10-24: why the estimate is what it is — the suppliers' average (whose: with their prices only) or my price. */
  estimate?: EstimateBasis;
  /** Owner 23:08: more is rented than wanted and the surplus is on rentals already picked up / returned — their suppliers. */
  lockedRentals?: Array<{ supplierName: string; reason: 'picked' | 'typed' }>;
  /** Equipment whose catalog has no rental price (it got 0 zł). */
  noCatalogPrice?: boolean;
  /** Owner 2026-11-04: from the packlist's „Pozycja własna” without a price — 0 zł „do uzupełnienia” (only for whoever sees prices). */
  needsPrice?: boolean;
  /** Review I2: made from the packlist's „Pozycja własna” (changed there; no „Dodaj do katalogu”). */
  fromPacklist?: boolean;
  /** Owner 2026-10-23 20:25: only in the answer of "Dodaj…" — the gear belongs to another category's section. */
  placement?: LinePlacement;
  version: number;
};

/**
 * Owner 2026-10-25 00:39: one holder of a short item over the line's days — an event (`eventId`: a link to its packlist;
 * null: „Inne wydarzenie”), a block („Blokada: <powód>” / „Inne zajęcie”) or a manual reservation, ahead of this event
 * in the queue (it takes the pieces).
 */
export type ShortageHolder = { eventId: string | null; name: string; firstDay: string; lastDay: string; quantity: number };

/** The basis of a shortage's estimate (owner 2026-10-24). */
export type EstimateBasis = { missing: number; source: 'suppliers' | 'own'; unitPriceGrosze: number; suppliers?: Array<{ supplierName: string; priceDayGrosze: number }> };

/** One rental of the event as a quote line shows it. */
export type LineRental = { id: string; /** null: a supplier off our list */ supplierId: string | null; supplierName: string; quantity: number; status: string; costGrosze: number; costMode?: 'cennik' | 'reczny'; /** owner 2026-10-24: the hover's „× cena/doba” (null: typed cost) */ priceDayGrosze?: number | null; /** owner 2026-11-01: a closed event's rental (an „Anulowane” one costs nothing) */ mark?: { kind: 'anulowane' | 'sprawdz'; label: string } };

/**
 * Where a line added by "Dodaj…" belongs (owner 2026-10-23 20:25): `suggest` — the quote has the section of its
 * category (the line stayed where asked); `created` — that section was added and the line went there; `category`
 * (owner 2026-10-24, a section without a category) — the quote has no section of its category and none was made.
 */
export type LinePlacement =
  | { kind: 'suggest' | 'created'; sectionId: string; sectionName: string; fromSectionId: string }
  | { kind: 'category'; categoryId: string; categoryName: string; fromSectionId: string };
/** A placement notice with the line it is about. */
export type Placed = LinePlacement & { lineId: string; lineName: string; lineVersion: number };

export type Totals = { netGrosze: number; vatGrosze: number; grossGrosze: number; discountsGrosze: number };

/** P8: `services` ("Obsługa") and `transport` are the fixed sections under the equipment. */
export type SectionKind = 'equipment' | 'services' | 'transport';
/** `categoryId`: the catalog category the section was made from (its quick picker suggests that gear first). */
export type Section = { id: string; name: string; sort: number; kind: SectionKind; categoryId?: string | null; lines: QuoteLine[] };

export type Version = {
  id: string;
  quoteId: string;
  number: number;
  state: VersionState;
  outdated: boolean;
  basedOnId: string | null;
  dayPctsBp: number[];
  defaultDaysMilli: number;
  discountBp?: number;
  vatBp: number;
  hideZeroLines: boolean;
  notes: string;
  signature: string;
  sentAt: string | null;
  sentTo: string | null;
  sentChannel: string | null;
  /** The note of "Oznacz jako wysłaną" (an e-mail: "DW: …"). */
  sentNote: string;
  /** F30: where the e-mail of "Wyślij do klienta" is; null when sent another way. */
  emailStatus?: EmailStatus | null;
  decidedAt: string | null;
  decisionNote: string;
  /** Why it was rejected (P8); null otherwise (also for versions rejected before P8). */
  decisionReason?: RejectReason | null;
  totals?: Totals;
  /** F29: "Koszt / Wynik / Marża" (sensitive; absent when hidden). */
  costs?: { costGrosze: number; resultGrosze: number; marginBp: number | null; /** "Sam koszt u dostawcy" (in `costGrosze` too) */ supplierOnlyGrosze?: number; /** rentals of items without an own line (in `costGrosze` too) */ otherRentalsGrosze?: number; /** owner 2026-10-24 10:08: estimates of shortages (in `costGrosze` too) */ estimatedGrosze?: number };
  sections: Section[];
  /** Review N2: the quote's rentals picked up or returned that the governing version no longer has (kept, flagged). */
  rentalsOutside?: Array<{ id: string; name: string; supplierName: string; quantity: number; status: string }>;
  /** Owner 2026-10-23: a sent (or decided) version counted its transport with other km — the event's km now. */
  transportKmNow?: number | null;
  /** Owner 2026-10-21 23:48: a draft's groups of repeated lines ("Scal powtarzające się pozycje (N)"); absent otherwise. */
  duplicates?: number;
  /** Who made the version ("Info", owner 2026-10-22); null when the account is gone. */
  authorName?: string | null;
  version: number;
};

export type EmailStatus = 'queued' | 'sent' | 'failed';
export const EMAIL_STATUS_LABELS: Record<EmailStatus, string> = { queued: 'w kolejce', sent: 'wysłano', failed: 'błąd' };
/** What "Wyślij do klienta" starts with (F30). */
export type SendPreview = { to: string; subject: string; body: string };

/** After sending or accepting: why the event's status could not follow (F23). */
export type TransitionResult = Version & { eventStatusProblem?: 'planning_incomplete' | 'invalid_transition' };

/** One position that differs between two versions; amounts only for whoever may see the lines' "Kwota netto". */
export type CompareRow = { key: string; name: string; quantityA: number; quantityB: number; daysMilliA: number; daysMilliB: number; netA?: number; netB?: number };

export type VersionSummary = { id: string; number: number; state: VersionState; outdated: boolean; sentAt: string | null };
/** `responsibleUserId`/`responsibleName`: "Odpowiedzialny za wycenę" (P16), absent when that field is hidden. */
export type Quote = { id: string; eventId: string; subtitle?: string; syncFromPacklist?: boolean; syncFromCrew?: boolean; createdBy?: string; responsibleUserId?: string | null; responsibleName?: string | null; version: number };
/** `heldByOther` (P16): the event has a quote someone else holds (scope "własne"); nothing else of it is sent. */
/**
 * `packlistDiffers` (F27): the current version and the packlist differ; `crewDiffers` / `crewNoService` (Obsada →
 * kosztorys): it differs from the crew, functions without a service. Absent for a role that may not compare them.
 */
export type QuoteOfEvent = { quote: Quote | null; versions: VersionSummary[]; current: Version | null; heldByOther?: true; packlistDiffers?: boolean };

export type QuoteListRow = {
  quoteId: string;
  eventId: string;
  eventName: string;
  eventDeleted: boolean;
  startsAt: string | null;
  endsAt: string | null;
  clientName: string;
  lastVersion: number;
  lastVersionId: string;
  state: VersionState;
  /** When the last version was sent (null for a draft). */
  sentAt: string | null;
  /** The number of the accepted version, when it is not the last one shown. */
  acceptedVersion: number | null;
  totals?: Totals;
  /** The quote's own `version` ("Usuń wycenę…" sends it). */
  quoteVersion: number;
  /** "Ukryj na liście" (owner 2026-10-24); absent for a role that may not see the field. */
  hidden?: boolean;
};

export const QUOTES_KEY = ['quotes'] as const;
export const quoteKey = (eventId: string) => [...QUOTES_KEY, 'event', eventId] as const;
export const versionKey = (versionId: string) => [...QUOTES_KEY, 'version', versionId] as const;

/** 1000 -> "10" (percent of a discount), 2300 -> "23". */
export const bpToPct = (bp: number): string => formatDecimal(bp / 100);
/** "10" / "12,5" / "10%" -> bp (0–100%), null when not valid. */
export function pctToBp(text: string): number | null {
  const t = text.trim().replace(/%$/, '').trim();
  if (t === '') return 0;
  const bp = parseScaled(t, 2, { maxIntegerDigits: 3 });
  return bp === null || bp > 10000 ? null : bp;
}
